import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CircleUser,
  LogOut,
  Package,
  Menu as MenuIcon,
  ChevronRight,
} from "lucide-react";
import { BottomSheet } from "./BottomSheet";
import { Avatar } from "./Avatar";
import { useAuthStore } from "@/lib/auth-store";
import { doLogout } from "@/lib/session";

/**
 * Mobile-only account menu. Lives in the top-right corner of the
 * viewport on viewports below `lg`. Opens a bottom sheet with quick
 * access to the screens the 4-slot tab bar can't reach: Services,
 * Profile, and Log out.
 *
 * On desktop the SideNav already exposes all of these, so this whole
 * component is hidden.
 */
export function MobileAccountMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Account menu"
        onClick={() => setOpen(true)}
        className="lg:hidden fixed top-3 right-3 z-[60] w-10 h-10 rounded-full bg-surface/95 backdrop-blur border border-[var(--color-border)] shadow-sm grid place-items-center text-[var(--color-teal-700)] font-semibold text-[14px] active:scale-[0.97] transition overflow-hidden"
      >
        <Avatar user={user} size={40} className="text-[var(--color-teal-700)]" />
      </button>
      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title="Account"
        snapPoint="half"
      >
        <div className="space-y-1 pb-2">
          <div className="px-1 py-2 mb-2 border-b border-[var(--color-border)]">
            <p className="text-[15px] font-semibold truncate">{user.name}</p>
            <p className="text-[12px] text-[var(--color-text-muted)] truncate font-mono">
              {user.email}
            </p>
          </div>
          <MenuRow
            icon={<Package size={18} />}
            label="Services"
            onClick={() => {
              setOpen(false);
              navigate("/services");
            }}
          />
          <MenuRow
            icon={<CircleUser size={18} />}
            label="Profile"
            onClick={() => {
              setOpen(false);
              navigate("/profile");
            }}
          />
          <MenuRow
            icon={<LogOut size={18} />}
            label="Log out"
            danger
            onClick={() => void doLogout()}
          />
          <p className="text-[11px] text-[var(--color-text-subtle)] text-center pt-3 pb-1">
            v0.1.0 · DYUTHI TECH SOLUTIONS
          </p>
        </div>
      </BottomSheet>
    </>
  );
}

function MenuRow({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 rounded-md px-3 py-3 text-[15px] hover:bg-[var(--color-surface-2)] active:bg-[var(--color-surface-hover)] transition ${
        danger ? "text-[var(--color-danger)]" : "text-[var(--color-text)]"
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 text-left">{label}</span>
      <ChevronRight
        size={16}
        className="text-[var(--color-text-muted)] shrink-0"
        aria-hidden
      />
    </button>
  );
}

// Avoid unused import warning if user removes the Link reference
void Link;
